import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Building2, 
  MapPin, 
  Calendar,
  Euro,
  Heart,
  Loader2,
  ExternalLink,
  Inbox
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des favoris");
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (tenderId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/favorites/${tenderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(prev => prev.filter(f => f.id !== tenderId));
      toast.success("Retiré des favoris");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-display text-3xl font-bold text-slate-900">
            Mes favoris
          </h1>
          <p className="text-slate-600 mt-1">
            {favorites.length} marché{favorites.length > 1 ? "s" : ""} sauvegardé{favorites.length > 1 ? "s" : ""}
          </p>
        </motion.div>

        {favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Inbox className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
                  Aucun favori
                </h3>
                <p className="text-slate-600 mb-6">
                  Commencez par explorer les marchés recommandés et ajoutez vos favoris.
                </p>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-violet-700 hover:bg-violet-800 text-white"
                  data-testid="go-to-dashboard-btn"
                >
                  Voir mes recommandations
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {favorites.map((tender, index) => (
              <motion.div
                key={tender.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className="border-l-4 border-l-red-400 hover:shadow-lg transition-all cursor-pointer"
                  data-testid={`favorite-card-${tender.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-violet-100 text-violet-700">
                            {tender.sector}
                          </Badge>
                          <Badge variant="outline" className="text-slate-600">
                            {tender.sub_sector}
                          </Badge>
                        </div>
                        
                        <h3 
                          className="font-display text-lg font-semibold text-slate-900 mb-2 cursor-pointer hover:text-violet-700"
                          onClick={() => navigate(`/tender/${tender.id}`)}
                        >
                          {tender.title}
                        </h3>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {tender.buyer}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {tender.region}
                          </span>
                          <span className="flex items-center gap-1">
                            <Euro className="w-4 h-4" />
                            {tender.estimated_value}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(tender.deadline).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFavorite(tender.id)}
                          className="text-red-500"
                          data-testid={`remove-favorite-${tender.id}`}
                        >
                          <Heart className="w-5 h-5 fill-current" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/tender/${tender.id}`)}
                          className="text-slate-400 hover:text-violet-600"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FavoritesPage;
